import * as React from 'react';
import { useDataProvider } from 'react-admin';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import type { WidgetProps } from '@rjsf/utils';

interface UserOption {
    id: string;
    label: string;
}

// Admin (infra) application GUID — restrict the search to its users only.
const INFRA_APPLICATION_GUID = '13b312b5-367c-4c74-bb77-bdb34df4e721';

const toOption = (user: any): UserOption => {
    const firstname = user?.public?.firstname ?? '';
    const lastname = user?.public?.lastname ?? '';
    const name = `${firstname} ${lastname}`.trim();
    const username = user?.username ? ` (${user.username})` : '';
    return {
        id: user.id ?? user.guid,
        label: `${name || user.id || user.guid}${username}`,
    };
};

/**
 * RJSF widget that lets the admin search for a user by name/username instead of
 * pasting a raw GUID. The widget value is the selected user's GUID.
 */
const UserSearchWidget = (props: WidgetProps) => {
    const { value, onChange, label, required, disabled, readonly, rawErrors } =
        props;
    const dataProvider = useDataProvider();

    const [selected, setSelected] = React.useState<UserOption | null>(null);
    const [options, setOptions] = React.useState<UserOption[]>([]);
    const [inputValue, setInputValue] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    // Resolve the label for an already-stored GUID value.
    React.useEffect(() => {
        let active = true;
        if (!value) {
            setSelected(null);
            return;
        }
        if (selected?.id === value) return;
        dataProvider
            .getOne('user', { id: value })
            .then(({ data }: { data: any }) => {
                if (active && data) setSelected(toOption(data));
            })
            .catch(() => {
                // Keep the raw GUID visible if the user can't be fetched.
                if (active) setSelected({ id: value, label: value });
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Debounced free-text search against the user resource.
    React.useEffect(() => {
        const term = inputValue.trim();
        if (term.length < 2) {
            setOptions([]);
            return;
        }
        let active = true;
        setLoading(true);
        const handle = setTimeout(() => {
            dataProvider
                .getList('user', {
                    pagination: { page: 1, perPage: 20 },
                    sort: { field: 'public.lastname', order: 'ASC' },
                    filter: {
                        q: term,
                        application: INFRA_APPLICATION_GUID,
                        status: '0,1',
                    },
                })
                .then(({ data }: { data: any[] }) => {
                    if (active) setOptions(data.map(toOption));
                })
                .catch(() => {
                    if (active) setOptions([]);
                })
                .finally(() => {
                    if (active) setLoading(false);
                });
        }, 300);
        return () => {
            active = false;
            clearTimeout(handle);
        };
    }, [inputValue, dataProvider]);

    return (
        <Autocomplete<UserOption, false, false, false>
            value={selected}
            options={options}
            loading={loading}
            disabled={disabled || readonly}
            filterOptions={x => x}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            getOptionLabel={option => option.label}
            onChange={(_event, newValue) => {
                setSelected(newValue);
                onChange(newValue ? newValue.id : undefined);
            }}
            onInputChange={(_event, newInput) => setInputValue(newInput)}
            noOptionsText="Hae käyttäjää nimellä tai käyttäjätunnuksella"
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    error={Array.isArray(rawErrors) && rawErrors.length > 0}
                    helperText={
                        Array.isArray(rawErrors) && rawErrors.length > 0
                            ? rawErrors[0]
                            : undefined
                    }
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={20}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default UserSearchWidget;
